import { OwcListItem, OwcSelectDropdown, OwcTypography } from "@one/react";
import moment from "moment-timezone";
import { useEffect, useMemo, useState } from "react";
import { RRule } from "rrule";
import { useRecurringBooking } from "./Context/RecurringBookingContext";
import { useSelector } from "react-redux";

export interface RepetitionOption {
  name: string;
  value: string;
  disabled?: boolean;
  RRuleValue?: number;
  RRuleByweekday?: string[];
  RRuleBysetpos?: number[];
}

export const REPETITION_TYPES = {
  DOES_NOT_REPEAT: 'does_not_repeat',
  DAILY_WEEKDAYS: 'daily_weekdays',
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly'
} as const;

const RRULE_WEEKDAYS = {
  0: RRule.SU,
  1: RRule.MO,
  2: RRule.TU,
  3: RRule.WE,
  4: RRule.TH,
  5: RRule.FR,
  6: RRule.SA
};

const RRULE_CONSTANTS = {
  WEEKDAYS: [0, 1, 2, 3, 4],
  WEEKLY: RRule.WEEKLY,
  DAILY: RRule.DAILY,
  MONTHLY: RRule.MONTHLY,
  YEARLY: RRule.YEARLY
};

const getMaxOccurrences = (repetitionType: string): number | null => {
  const limits = {
    [REPETITION_TYPES.DAILY]: 30,
    [REPETITION_TYPES.DAILY_WEEKDAYS]: 30,
    [REPETITION_TYPES.WEEKLY]: 52,
    [REPETITION_TYPES.MONTHLY]: 24,
    [REPETITION_TYPES.YEARLY]: 3
  };

  return limits[repetitionType as keyof typeof limits] || null;
};

const weekCounter = ['first', 'second', 'third', 'fourth', 'last'];

const getWeekdayOccurrenceEfficient = (dateInput: string | Date) => {
  const date = moment(dateInput);


  const targetWeekday = date.day();
  const dayOfMonth = date.date();
  const startOfMonth = date.clone().startOf('month');
  const firstOccurrenceDay = startOfMonth.clone();

  while (firstOccurrenceDay.day() !== targetWeekday) {
    firstOccurrenceDay.add(1, 'day');
  }

  const daysDifference = dayOfMonth - firstOccurrenceDay.date();
  const occurrence = Math.floor(daysDifference / 7) + 1;

  return occurrence;
};

const isLongerThanDay = (startDate: string | Date, endDate: string | Date): boolean => {
  const start = moment(startDate);
  const end = moment(endDate);
  return end.diff(start, 'days', true) > 1;
};

const isLongerThanWeek = (startDate: string | Date, endDate: string | Date): boolean => {
  const start = moment(startDate);
  const end = moment(endDate);
  return end.diff(start, 'weeks', true) > 1;
};

const isLongerThanMonth = (startDate: string | Date, endDate: string | Date): boolean => {
  const start = moment(startDate);
  const end = moment(endDate);
  return end.diff(start, 'hours', true) > 672; // 28 days in hours
};

const createRepetitionOptions = (
  dayName = 'Tuesday',
  monthName = 'March',
  weekNumber = 'second',
  dayNumber = '1st',
  disabledOptions: string[] = [],
  selectedDate: string | Date,
  dayDifference: number = 0
): RepetitionOption[] => {

  // Calculate weekday and occurrence from the selected date
  const date = moment(selectedDate);
  const weekdayIndex = date.day(); // 0 = Sunday, 1 = Monday, etc.
  const weekdayRRule = dayDifference === 1 ? RRULE_WEEKDAYS[weekdayIndex - dayDifference] : RRULE_WEEKDAYS[weekdayIndex];
  const occurrence = getWeekdayOccurrenceEfficient(selectedDate);

  // For bysetpos, use the occurrence number (1, 2, 3, 4) or -1 for last
  const bysetposValue = occurrence === 5 ? -1 : occurrence;

  const options = [
    {
      name: 'Does not repeat',
      value: REPETITION_TYPES.DOES_NOT_REPEAT
    },
    {
      name: 'Daily on workdays (Mon-Fri)',
      value: REPETITION_TYPES.DAILY_WEEKDAYS,
      RRuleValue: RRULE_CONSTANTS.WEEKLY,
      RRuleByweekday: RRULE_CONSTANTS.WEEKDAYS.map(day => {
        const adjustedDay = day - dayDifference;
        // Handle both negative and positive overflow using modulo
        return ((adjustedDay % 7) + 7) % 7;
      })
    },
    {
      name: 'Daily (Mon-Sun)',
      value: REPETITION_TYPES.DAILY,
      RRuleValue: RRULE_CONSTANTS.DAILY
    },
    {
      name: `Weekly on ${dayName}`,
      value: REPETITION_TYPES.WEEKLY,
      RRuleValue: RRULE_CONSTANTS.WEEKLY,
      RRuleByweekday: [weekdayRRule]
    },
    {
      name: `Monthly on the ${weekNumber} ${dayName}`,
      value: REPETITION_TYPES.MONTHLY,
      RRuleValue: RRULE_CONSTANTS.MONTHLY,
      RRuleByweekday: [weekdayRRule],
      RRuleBysetpos: [bysetposValue]
    },
    {
      name: `Yearly on ${monthName} ${dayNumber}`,
      value: REPETITION_TYPES.YEARLY,
      RRuleValue: RRULE_CONSTANTS.YEARLY
    }
  ];

  return options.map(option => ({
    ...option,
    disabled: disabledOptions.includes(option.value)
  }));
};

const getDayDifferenceForByWeekDay = (selectedStartDate: string | Date, displayTimeZone: string) => {
  const originalDay = moment(selectedStartDate).utc().date();
  const selectedStartDateToTimezone = moment(selectedStartDate).tz(displayTimeZone);
  const timezoneDay = selectedStartDateToTimezone.date();
  const dayDifference = timezoneDay - originalDay;

  return dayDifference;
}

export const RecurringBookingPicker = () => {
  const { setSelectedOccurances, setSelectedRepetition, temporarySelectedDates, selectedOccurances, selectedRepetition } = useRecurringBooking();
  const storeUser = useSelector((store: { user: { timezone: string } }) => store.user);
  const displayTimeZone = storeUser.timezone || "UTC";
  const selectedStartDate = temporarySelectedDates[0];
  const selectedEndDate = temporarySelectedDates[1];
  const longerThanDay = isLongerThanDay(selectedStartDate, selectedEndDate);
  const longerThanWeek = isLongerThanWeek(selectedStartDate, selectedEndDate);
  const longerThanMonth = isLongerThanMonth(selectedStartDate, selectedEndDate);
  const dayDifference = getDayDifferenceForByWeekDay(selectedStartDate, displayTimeZone);
  const disabledOptions: string[] = [];

  if (longerThanDay) {
    disabledOptions.push(REPETITION_TYPES.DAILY, REPETITION_TYPES.DAILY_WEEKDAYS);
  }

  // If event is longer than a week, disable weekly option
  if (longerThanWeek) {
    disabledOptions.push(REPETITION_TYPES.WEEKLY);
  }

  // If event is longer than a month, disable monthly option
  if (longerThanMonth) {
    disabledOptions.push(REPETITION_TYPES.MONTHLY, REPETITION_TYPES.YEARLY);
  }

  const selectedDate = moment(selectedStartDate).tz(displayTimeZone);

  const dayName = selectedDate.format('dddd');
  const dayNumber = selectedDate.format('Do');
  const monthName = selectedDate.format('MMMM');
  const weekNumber = weekCounter[getWeekdayOccurrenceEfficient(selectedDate.toDate()) - 1] || 'last';

  const repetitionOptions = useMemo(() =>
    createRepetitionOptions(dayName, monthName, weekNumber, dayNumber, disabledOptions, selectedStartDate, dayDifference),
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [dayName, monthName, weekNumber, dayNumber, disabledOptions.join(','), selectedStartDate]
  );

  const [selectedRepetitionValue, setSelectedRepetitionValue] = useState<string>(selectedRepetition?.value || REPETITION_TYPES.DOES_NOT_REPEAT);
  const currentSelectedRepetition = useMemo(() => {
    const current = repetitionOptions.find(option => option.value === selectedRepetitionValue);

    if (!current || current.disabled) {
      return repetitionOptions.find(option => !option.disabled) || repetitionOptions[0];
    }

    return current;
  }, [repetitionOptions, selectedRepetitionValue]);

  useEffect(() => {
    if (currentSelectedRepetition.value !== selectedRepetitionValue) {
      setSelectedRepetitionValue(currentSelectedRepetition.value);
      setSelectedOccurances(2);

      setSelectedRepetition(currentSelectedRepetition);
      setSelectedOccurances(2);
    }
  }, [currentSelectedRepetition.value, selectedRepetitionValue, setSelectedRepetition, setSelectedOccurances, currentSelectedRepetition]);

  useEffect(() => {
    setSelectedOccurances(selectedOccurances);
  }, [selectedOccurances, setSelectedOccurances]);

  useEffect(() => {
    setSelectedRepetition(currentSelectedRepetition);
  }, [currentSelectedRepetition, setSelectedRepetition]);

  const occurrenceOptions = useMemo(() => {
    const maxOccurrences = getMaxOccurrences(currentSelectedRepetition.value);
    if (!maxOccurrences) return [];

    return Array.from({ length: maxOccurrences - 1 }, (_, i) => i + 2);
  }, [currentSelectedRepetition.value]);

  const handleRepetitionChange = (selectedIndexes: number[]) => {
    const selectedIndex = selectedIndexes[0];
    if (selectedIndex !== undefined) {
      const selectedOption = repetitionOptions[selectedIndex];
      if (!selectedOption.disabled) {
        setSelectedRepetitionValue(selectedOption.value);
        setSelectedOccurances(2);

        setSelectedRepetition(selectedOption);
        setSelectedOccurances(2);
      }
    }
  };

  const handleOccurrenceChange = (selectedIndexes: number[]) => {
    const selectedIndex = selectedIndexes[0];
    if (selectedIndex !== undefined) {
      const newOccurrences = occurrenceOptions[selectedIndex];
      setSelectedOccurances(newOccurrences);
    }
  };

  const showOccurrences = currentSelectedRepetition.value !== REPETITION_TYPES.DOES_NOT_REPEAT;

  return (
    <div style={{
      width: 'fit-content',
      marginTop: 74,
      padding: 16,
      border: '1px solid rgba(0,0,0,0.2)',
      borderRadius: 4,
      backgroundColor: 'white',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <OwcTypography
        variant="body2"
        style={{ marginBottom: 8 }}
      >
        Define repetition
      </OwcTypography>

      <OwcSelectDropdown
        label="Repetition"
        disabled={!selectedStartDate || !selectedEndDate || longerThanMonth}
        value={[currentSelectedRepetition.value]}
        onSelectChange={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          handleRepetitionChange(ev.detail);
        }}
        style={{ zIndex: 100000 }}
      >
        {repetitionOptions.map(({ name, value, disabled }) => (
          <OwcListItem
            key={value}
            value={value}
            disabled={disabled}
            style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <OwcTypography
              variant="body2"
              style={disabled ? { color: '#999' } : {}}
            >
              {name}
            </OwcTypography>
          </OwcListItem>
        ))}
      </OwcSelectDropdown>
      {showOccurrences && (
        <div style={{
          marginTop: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <OwcSelectDropdown
            value={[selectedOccurances.toString()]}
            onSelectChange={(ev) => handleOccurrenceChange(ev.detail)}
            style={{ width: 100 }}
          >
            {occurrenceOptions.map(count => (
              <OwcListItem
                key={count}
                value={count.toString()}
              >
                <OwcTypography
                  variant="body2"
                >
                  {count}
                </OwcTypography>
              </OwcListItem>
            ))}
          </OwcSelectDropdown>

          <OwcTypography
            variant="body2"
          >
            occurrences
          </OwcTypography>
        </div>
      )}
    </div>
  );
};
